// ============================================================================
// CANCUN100 — AgentesTTTab.js  (Pestaña "👥 Agentes" · Bloque 3 → Bloque 20: por equipo)
// ----------------------------------------------------------------------------
// prop equipo = 'top_tours' (Maggie y Marco) | 'sala' (Tere y Marco). Mismo diseño y funciones para los dos:
// alta (con PIN o con invitación), edición, contraseña, ⏸ Pausar / ▶ Reactivar (activo=false/true, nunca borrar),
// historial e invitaciones. La modalidad Becados/50-50 y "Mi comisión de supervisión" son solo de Top Tours.
// Sala = tipo_agente 'sala' o nulo (fuera top_tours y ventas_admin).
// ESCRITURA SOLO en agentes: .insert() (alta) y .update() por id, SIEMPRE con
// actualizado_por. NUNCA .delete(): dar de baja = activo=false.
// LECTURA: agentes (sin pin, nunca), agentes_historial (lo llena un trigger),
// ventas_top_tours_v2 (conteo de folios), reservas/cotizaciones (prefijos usados).
// Bloque 12 §1f: el correo público vive en `email_publico`. `telefono` es LEGADO (lo usa login_agente):
// se sigue escribiendo igual que hoy para no romper nada, pero ya no se muestra ni se publica.
// Colores: objeto C + tokens Aurelia. No convierte C a CSS.
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA } from './tema';
import { MODALIDAD_TT } from './comisionTT'; // mapa único de etiquetas (basico = Becados, premier = 50-50)
import Segmentado from './Segmentado';
import CampoSecreto from './CampoSecreto'; // Hotfix 15-oct: ojito
import useBorrador, { claveBorrador, BandaBorrador } from './useBorrador'; // Bloque 27
import { EstadoFicha, ModalVerFicha, puedeVerSensiblesRol } from './FichaAdmin'; // Bloque 28
import { cargarFichas, solicitarFicha, fichaCompleta } from './ficha'; // Bloque 28
import EquipoAgentes, { ResultadoAlta, llamarAltaAgente, correoC100, equiposDeRol, AVISO_DOS_CORREOS, komitoEnProceso, EN_PROCESO_MS, ChipAcceso } from './EquipoAgentes'; // Bloque 52 · 52-B
const INICIAL_ALTA = { nombre: '', correo: '', prefijo: '', prefijoManual: false, modalidad: 'basico' };
const esVacioAlta = (e) => !e || (!String(e.nombre || '').trim() && !String(e.correo || '').trim());

// La paleta global no trae rojo: rojo vivo propio para bajas/errores de esta pestaña.

const COLS = 'id,nombre,tipo_agente,email_publico,telefono,folio_prefix,modalidad_tt,activo,tarifa_pax,created_at,alta_por,baja_at,baja_por,baja_motivo,auth_uid,login_pin_activo,invitacion_enviada_en,invitacion_por,contrasena_creada_en,ficha_solicitada_en,ficha_solicitada_por,ficha_completa_en,foto_url,puede_rp_por_fuera,es_fantasma,pausa_at,pausa_por,pausa_motivo'; // Bloque 28: + ficha · Bloque 36: + RP · Bloque 52: + fantasma y pausa
const CAMPO_LABEL = { activo: 'Activo', modalidad_tt: 'Modalidad', nombre: 'Nombre', folio_prefix: 'Prefijo', telefono: 'Correo (legado)', email_publico: 'Correo público', tarifa_pax: 'Tarifa por pax', tipo_agente: 'Tipo de agente', pin: 'Contraseña' };
// Bloque 19: el correo duplicado lo frena la base (trg_agentes_correo_unico → CORREO_DUPLICADO / 23505).
const MSG_CORREO_DUP = 'Ese correo ya está registrado con otro agente.';
const esCorreoDup = (e) => !!e && (e.code === '23505' || /CORREO_DUPLICADO/i.test(String(e.message || '')));
const pinAleatorio = () => String(Math.floor(100000 + Math.random() * 900000)); // invitados: PIN inútil (login_pin_activo=false)

const norm = (s) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const correoValido = (c) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(c || '').trim());
const fmtFecha = (f) => {
  if (!f) return '—';
  try { return new Date(f).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return String(f); }
};
const valorHist = (campo, v) => {
  if (v == null || v === '') return '—';
  if (campo === 'modalidad_tt') return MODALIDAD_TT[v] || v;
  if (campo === 'activo') return v === true || v === 'true' ? 'Activo' : 'Baja';
  return String(v);
};

// Modal a nivel de módulo (si se definiera dentro del render, los inputs perderían el foco en cada tecla).
const Modal = ({ C, busy, onClose, titulo, children, ancho = 460 }) => (
  <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: ancho, maxHeight: '88vh', overflowY: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</div>
        <button type="button" onClick={onClose} disabled={busy} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

// ── Bloque 4 (T5): "Mi comisión de supervisión" — solo lectura de la vista comisiones_supervisor_tt
// (estado = 'enviado_pago', cancelado = false). El pago a la supervisora entra en contabilidad más adelante.
const F2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pesos = (n) => '$' + F2.format(Number(n) || 0);
const pad2 = (n) => String(n).padStart(2, '0');
const ymdLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fechaSup = (r) => String(r.fecha_cancun || '').slice(0, 10);
const rangoPeriodo = (modo, anchor) => {
  const a = new Date(anchor);
  if (modo === 'mes') {
    const ini = new Date(a.getFullYear(), a.getMonth(), 1), fin = new Date(a.getFullYear(), a.getMonth() + 1, 0);
    return { ini: ymdLocal(ini), fin: ymdLocal(fin), label: ini.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) };
  }
  const lunes = new Date(a); lunes.setDate(a.getDate() - ((a.getDay() + 6) % 7));
  const domingo = new Date(lunes); domingo.setDate(lunes.getDate() + 6);
  const fmtC = (d) => d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  return { ini: ymdLocal(lunes), fin: ymdLocal(domingo), label: `Semana ${fmtC(lunes)} – ${fmtC(domingo)}` };
};

function SupervisionTT({ C, card }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modo, setModo] = useState('semana'); // 'semana' | 'mes'
  const [anchor, setAnchor] = useState(() => new Date());
  useEffect(() => {
    (async () => {
      const { data, error: e } = await supabase.from('comisiones_supervisor_tt').select('*').eq('estado', 'enviado_pago').eq('cancelado', false);
      if (e) setError(e.message); else setRows(data || []);
      setLoading(false);
    })();
  }, []);
  const { ini, fin, label } = rangoPeriodo(modo, anchor);
  const enPeriodo = rows.filter((r) => { const d = fechaSup(r); return d >= ini && d <= fin; })
    .sort((a, b) => fechaSup(b).localeCompare(fechaSup(a)));
  const unidadesDe = (r) => Number(r.supervisor_unidades) || 0;
  const montoDe = (r) => Number(r.comision_supervisor) || 0;
  const totU = enPeriodo.reduce((s, r) => s + unidadesDe(r), 0);
  const totM = enPeriodo.reduce((s, r) => s + montoDe(r), 0);
  const mover = (dir) => setAnchor((a) => { const d = new Date(a); if (modo === 'mes') d.setMonth(d.getMonth() + dir); else d.setDate(d.getDate() + 7 * dir); return d; });
  const th = { padding: '6px 8px', textAlign: 'left', fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', fontSize: 12, color: C.text };
  return (
    <div style={{ ...card, marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>🧭 Mi comisión de supervisión</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="au-toggle">
            <button type="button" aria-pressed={modo === 'semana'} onClick={() => setModo('semana')}>Semana</button>
            <button type="button" aria-pressed={modo === 'mes'} onClick={() => setModo('mes')}>Mes</button>
          </div>
          <button type="button" className="au-btn au-btn--ghost" onClick={() => mover(-1)} style={{ padding: '4px 10px' }}>‹</button>
          <span style={{ fontSize: 12, color: C.text, fontWeight: 600, minWidth: 150, textAlign: 'center' }}>{label}</span>
          <button type="button" className="au-btn au-btn--ghost" onClick={() => mover(1)} style={{ padding: '4px 10px' }}>›</button>
        </div>
      </div>
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 14, fontSize: 12 }}>No se pudo leer comisiones_supervisor_tt: {error}</div>}
      <div style={{ display: 'flex', gap: 18, marginBottom: 14, flexWrap: 'wrap' }}>
        <div><div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Folios</div><div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{enPeriodo.length}</div></div>
        <div><div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>Unidades</div><div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{totU}</div></div>
        <div><div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>$ Supervisión</div><div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>{pesos(totM)}</div></div>
      </div>
      {loading ? <div style={{ color: C.muted, padding: 16 }}>Cargando…</div> : enPeriodo.length === 0 ? (
        <div style={{ color: C.muted, padding: 16, textAlign: 'center', fontSize: 12 }}>Sin folios enviados a pago en este periodo.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{['Fecha', 'Folio', 'Agente', 'Modalidad', 'Unidades', '$ Supervisión'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{enPeriodo.map((r, i) => (
              <tr key={r.reserva_id ?? i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ ...td, color: C.muted, whiteSpace: 'nowrap' }}>{fechaSup(r) || '—'}</td>
                <td style={{ ...td, fontFamily: 'var(--au-font-text)', fontWeight: 700, color: C.rosa }}>{r.folio || '—'}</td>
                <td style={td}>{r.agente || '—'}</td>
                <td style={td}>{MODALIDAD_TT[r.modalidad_tt] || '—'}</td>
                <td style={td}>{unidadesDe(r)}</td>
                <td style={{ ...td, fontWeight: 700, color: C.green }}>{pesos(montoDe(r))}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const perteneceA = (a, equipo) => (equipo === 'top_tours'
  ? a.tipo_agente === 'top_tours'
  : (a.tipo_agente == null || a.tipo_agente === '' || a.tipo_agente === 'sala'));
const NOMBRE_EQUIPO = { top_tours: 'Top Tours', sala: 'Sala de Ventas' };

export default function AgentesTTTab({ C: Cbase, loggedAgent = null, equipo = 'top_tours', pie = null, onAuditoria = null, enSandbox = false }) {
  // Bloque 52: rol del admin para la sección Equipo · Agentes y el alta con Komito (los permisos reales los aplica alta-agente)
  const rol = loggedAgent?.rol || null;
  const equiposAlta = equiposDeRol(rol);
  const [refrescarEquipo, setRefrescarEquipo] = useState(0);
  // Agentes que ya se manejan por app + Komito: los de Sala o Top Tours que no son fantasma (fuera del «Ver como»).
  const gestionaKomito = (a) => !enSandbox && !a.es_fantasma && (a.tipo_agente === 'sala' || a.tipo_agente === 'top_tours');
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const esTT = equipo === 'top_tours';
  const [verPausados, setVerPausados] = useState(false); // Bloque 20: sección "Pausados" plegada
  const quien = loggedAgent?.nombre || 'admin';
  const [lista, setLista] = useState([]);       // agentes Top Tours (activos e inactivos)
  const [todos, setTodos] = useState([]);       // TODOS los agentes (para validar nombre/prefijo/correo)
  const [folios, setFolios] = useState({});     // agente_id → nº de folios
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);     // { tipo, ag, ...campos }
  const [fichas, setFichas] = useState({});     // Bloque 28: agente_id → fila de agentes_ficha (sin sensibles si no puede verlos)
  const puedeVerSensibles = puedeVerSensiblesRol(loggedAgent?.rol); // Marco y Tere sí; Maggie no
  // Bloque 19: la Edge Function invitar-agente valida al admin con login_admin_v2 → se pide su contraseña
  // una vez y se guarda SOLO en memoria (se pierde al recargar; nunca en localStorage ni en la base).
  const credAdmin = useRef(null);               // { usuario, password }
  const prefijoTimer = useRef(null);
  // Bloque 27: borrador del alta (nunca el PIN). Se restaura al abrir "+ Dar de alta agente".
  const estadoAlta = modal && modal.tipo === 'alta' ? { nombre: modal.nombre || '', correo: modal.correo || '', prefijo: modal.prefijo || '', prefijoManual: !!modal.prefijoManual, modalidad: modal.modalidad || 'basico' } : INICIAL_ALTA;
  const ponerAlta = useCallback((d) => setModal((m) => (m && m.tipo === 'alta' ? { ...m, ...INICIAL_ALTA, ...(d || {}), pin: '', pin2: '', err: '', errs: [] } : m)), []);
  const borradorAlta = useBorrador(claveBorrador(`alta-${equipo}`, loggedAgent?.id), estadoAlta, ponerAlta, { activo: !!modal && modal.tipo === 'alta', inicial: INICIAL_ALTA, esVacio: esVacioAlta });

  const showToast = (tipo, msg) => { setToast({ tipo, msg }); setTimeout(() => setToast(null), 3800); };

  const load = useCallback(async () => {
    // Hotfix (15-oct): el select no traía tipo_agente y el filtro en cliente dejaba Top Tours vacío. Ahora el equipo se
    // filtra en la consulta (Sala = 'sala' o sin tipo) y todos (para validar nombre/prefijo/correo) sigue siendo la lista completa.
    const [all, equipoQ] = await Promise.all([
      supabase.from('agentes').select(COLS).order('activo', { ascending: false }).order('nombre'),
      (esTT ? supabase.from('agentes').select(COLS).eq('tipo_agente', 'top_tours') : supabase.from('agentes').select(COLS).or('tipo_agente.eq.sala,tipo_agente.is.null,tipo_agente.eq.'))
        .order('activo', { ascending: false }).order('nombre'),
    ]);
    if (all.error || equipoQ.error) { setError((all.error || equipoQ.error).message); setLoading(false); return; }
    setError('');
    const mios = (equipoQ.data || []).filter((a) => perteneceA(a, equipo));
    setLista(mios);
    setTodos(all.data || []);
    try { setFichas(await cargarFichas(mios.map((a) => a.id), puedeVerSensibles)); } catch (e) { setFichas({}); } // Bloque 28
    // Conteo de folios vigentes por agente (una fila vigente por folio; sin eliminados).
    // Top Tours → ventas_top_tours_v2; Sala → reservas (misma regla de vigencia).
    const conteos = await Promise.all(mios.map((a) => (esTT
      ? supabase.from('ventas_top_tours_v2').select('reserva_id', { count: 'exact', head: true }).eq('agente_id', a.id).eq('es_vigente', true).neq('estado', 'eliminado')
      : supabase.from('reservas').select('id', { count: 'exact', head: true }).eq('agente_id', a.id).eq('es_vigente', true).neq('estado', 'eliminado'))
      .then((r) => [a.id, r.error ? null : r.count])));
    setFolios(Object.fromEntries(conteos));
    setLoading(false);
  }, [equipo, esTT, puedeVerSensibles]);
  useEffect(() => { load(); }, [load]);
  // ── Bloque 28: ficha ──
  const pedirFicha = async (ag) => {
    setBusy(true);
    try { await solicitarFicha(ag.id, quien); showToast('ok', `Ficha solicitada a ${ag.nombre}`); await load(); }
    catch (e) { showToast('error', `No se pudo solicitar: ${e.message}`); }
    finally { setBusy(false); }
  };
  const sinFicha = (a) => a.activo !== false && !a.ficha_solicitada_en && !fichaCompleta(a, fichas[a.id]);
  const pedirFichaEquipo = async () => {
    const pendientes = lista.filter(sinFicha);
    if (!pendientes.length) return;
    if (!window.confirm(`¿Solicitar la ficha a ${pendientes.length} agente${pendientes.length !== 1 ? 's' : ''} de ${NOMBRE_EQUIPO[equipo]}? Verán "Completa tu ficha" al entrar.`)) return;
    setBusy(true);
    let ok = 0, fallos = 0;
    for (const a of pendientes) { try { await solicitarFicha(a.id, quien); ok++; } catch { fallos++; } }
    setBusy(false);
    showToast(fallos ? 'error' : 'ok', `Ficha solicitada a ${ok} agente${ok !== 1 ? 's' : ''}${fallos ? ` · ${fallos} sin poder` : ''}`);
    await load();
  };

  // ── Escrituras (siempre con actualizado_por) ──
  const actualizar = async (id, cambios, okMsg) => {
    setBusy(true);
    const { error: e } = await supabase.from('agentes').update({ ...cambios, actualizado_por: quien }).eq('id', id);
    setBusy(false);
    if (e) { showToast('error', esCorreoDup(e) ? MSG_CORREO_DUP : `No se pudo guardar: ${e.message}`); return false; }
    showToast('ok', okMsg);
    setModal(null);
    await load();
    return true;
  };

  // ── Validaciones ──
  // login_agente busca con LIKE '%texto%': un nombre no puede contener ni estar contenido en otro activo.
  const errorNombre = (nombre, excluirId) => {
    const n = norm(nombre);
    if (!n) return 'Escribe el nombre completo.';
    if (n.length < 3) return 'El nombre es demasiado corto.';
    const otros = todos.filter((a) => a.id !== excluirId);
    const igual = otros.find((a) => norm(a.nombre) === n);
    if (igual) return `Ya existe un agente llamado "${igual.nombre}"${igual.activo === false ? ' (dado de baja)' : ''}.`;
    const choca = otros.find((a) => a.activo !== false && (norm(a.nombre).includes(n) || n.includes(norm(a.nombre))));
    if (choca) return `El nombre choca con "${choca.nombre}": uno contiene al otro y el inicio de sesión los confundiría. Usa nombre y apellido distintos.`;
    return '';
  };
  const avisoCorreo = (correo, excluirId) => {
    const c = norm(correo);
    if (!c) return '';
    const dup = todos.find((a) => String(a.id) !== String(excluirId ?? '') && (norm(a.email_publico) === c || norm(a.telefono) === c)); // Hotfix: excluye el propio id; norm = minúsculas y sin espacios
    return dup ? `Aviso: este correo ya lo tiene "${dup.nombre}".` : '';
  };
  // Prefijo: formato + único contra TODOS los agentes + folios ya usados en reservas y cotizaciones.
  // Las cotizaciones usan "<prefijo>C-####": XY y XYC chocarían en el folio visible.
  const errorPrefijoLocal = (p) => {
    if (!/^[A-Z]{2,3}$/.test(p)) return 'El prefijo debe ser de 2 o 3 letras (A–Z), en mayúsculas.';
    const usado = todos.find((a) => String(a.folio_prefix || '').toUpperCase() === p);
    if (usado) return `El prefijo ${p} ya es de "${usado.nombre}"${usado.activo === false ? ' (dado de baja)' : ''}.`;
    if (p.length === 3 && p.endsWith('C')) {
      const base = todos.find((a) => String(a.folio_prefix || '').toUpperCase() === p.slice(0, 2));
      if (base) return `${p} chocaría con las cotizaciones de ${p.slice(0, 2)} ("${base.nombre}"), que usan folios ${p}-####.`;
    }
    const conC = todos.find((a) => String(a.folio_prefix || '').toUpperCase() === `${p}C`);
    if (conC) return `${p} chocaría con los folios de ${p}C ("${conC.nombre}").`;
    return '';
  };
  const errorPrefijoRemoto = async (p) => {
    const [r, c1, c2] = await Promise.all([
      supabase.from('reservas').select('id').like('folio_personalizado', `${p}-%`).limit(1),
      supabase.from('cotizaciones').select('id').like('folio', `${p}C-%`).limit(1),
      supabase.from('cotizaciones').select('id').like('folio', `${p}-%`).limit(1),
    ]);
    if (r.error || c1.error || c2.error) return 'No se pudo verificar el prefijo contra los folios existentes. Intenta de nuevo.';
    if ((r.data || []).length) return `Ya hay reservas con folios ${p}-####. Elige otro prefijo.`;
    if ((c1.data || []).length || (c2.data || []).length) return `Ya hay cotizaciones con folios de ${p}. Elige otro prefijo.`;
    return '';
  };

  // ── Acciones ──
  const guardarModalidad = (ag) => {
    const nueva = ag.modalidad_tt === 'premier' ? 'basico' : 'premier';
    return actualizar(ag.id, { modalidad_tt: nueva }, `${ag.nombre}: ahora ${MODALIDAD_TT[nueva]}`);
  };
  const guardarEdicion = (m) => {
    const eN = errorNombre(m.nombre, m.ag.id);
    if (eN) { setModal({ ...m, err: eN }); return; }
    if (!correoValido(m.correo)) { setModal({ ...m, err: 'El correo no tiene un formato válido.' }); return; }
    actualizar(m.ag.id, { nombre: m.nombre.trim(), email_publico: m.correo.trim(), telefono: m.correo.trim() }, `Datos de ${m.nombre.trim()} actualizados`);
  };
  const guardarPin = (m) => {
    if (String(m.pin || '').length < 6) { setModal({ ...m, err: 'La contraseña debe tener al menos 6 caracteres.' }); return; }
    if (m.pin !== m.pin2) { setModal({ ...m, err: 'Las contraseñas no coinciden.' }); return; }
    actualizar(m.ag.id, { pin: m.pin }, `Contraseña de ${m.ag.nombre} actualizada`);
  };
  const guardarBaja = (m) => {
    if (!String(m.motivo || '').trim()) { setModal({ ...m, err: 'El motivo es obligatorio.' }); return; }
    actualizar(m.ag.id, { activo: false, baja_at: new Date().toISOString(), baja_por: quien, baja_motivo: m.motivo.trim() }, `${m.ag.nombre} en pausa`);
  };
  const guardarReactivar = (ag) => actualizar(ag.id, { activo: true, baja_at: null, baja_por: null, baja_motivo: null }, `${ag.nombre} reactivado`);
  const abrirHistorial = async (ag) => {
    setModal({ tipo: 'historial', ag, cargando: true, filas: [] });
    const { data, error: e } = await supabase.from('agentes_historial').select('*').eq('agente_id', ag.id).order('en', { ascending: false });
    setModal({ tipo: 'historial', ag, cargando: false, filas: data || [], err: e ? e.message : '' });
  };
  // Bloque 52: un solo camino. Se crea la fila en agentes como hoy (PIN aleatorio inútil: el login por PIN está apagado
  // desde B36) y luego alta-agente «crear» manda las dos invitaciones (Supabase Auth y Komito). Correo solo @cancun100.com,
  // en minúsculas, y casilla obligatoria «Ya creé este correo en Titan». El equipo es fijo salvo para Marco.
  const guardarAlta = async (m) => {
    const eq = equiposAlta.length > 1 ? (m.equipoAlta || equipo) : (equiposAlta[0] || equipo);
    const ttAlta = eq === 'top_tours';
    const nombre = String(m.nombre || '').trim();
    const correo = String(m.correo || '').trim().toLowerCase();
    const prefijo = String(m.prefijo || '').trim().toUpperCase();
    const errs = [];
    if (!equiposAlta.includes(eq)) errs.push('Tu usuario no puede dar de alta agentes en ese equipo.');
    const eN = errorNombre(nombre, null); if (eN) errs.push(eN);
    if (!correoC100(correo)) errs.push('El correo debe ser @cancun100.com (en minúsculas), el mismo que creaste en Titan.');
    else if (avisoCorreo(correo, null)) errs.push(MSG_CORREO_DUP);
    const eP = errorPrefijoLocal(prefijo); if (eP) errs.push(eP);
    if (!m.titan) errs.push('Marca «Ya creé este correo en Titan»: sin el correo, Komito no puede mandar su invitación.');
    if (errs.length) { setModal({ ...m, errs }); return; }
    setBusy(true);
    const eR = await errorPrefijoRemoto(prefijo);
    if (eR) { setBusy(false); setModal({ ...m, errs: [eR] }); return; }
    const { data: nuevo, error: e } = await supabase.from('agentes').insert({
      nombre, email_publico: correo, telefono: correo, tipo: 'simple', tipo_agente: eq,
      ...(ttAlta ? { modalidad_tt: m.modalidad || 'basico' } : {}), // Sala: sin modalidad
      pin: pinAleatorio(), login_pin_activo: false, comision_rate: 0.40, tarifa_pax: ttAlta ? 50 : 75, folio_prefix: prefijo, activo: true, tema: 'dark',
      alta_por: quien, actualizado_por: quien,
    }).select('id').single();
    if (e) { setBusy(false); setModal({ ...m, errs: [esCorreoDup(e) ? MSG_CORREO_DUP : `No se pudo dar de alta: ${e.message}`] }); return; }
    borradorAlta.limpiar(); // Bloque 27
    const alta = { tipo: 'alta-ok', nombre, prefijo, modalidad: m.modalidad || 'basico', equipoAlta: eq, agId: nuevo.id };
    if (enSandbox) { setBusy(false); setModal({ ...alta, sandbox: true }); await load(); return; } // «Ver como»: no se llama a Komito ni se invita
    setModal({ ...alta, enviando: true });
    const res = await llamarAltaAgente({ accion: 'crear', agente_id: nuevo.id });
    setBusy(false);
    setModal({ ...alta, res });
    if (komitoEnProceso(res)) consultarTrasProceso(nuevo.id);
    setRefrescarEquipo((x) => x + 1);
    await load();
  };
  // EN_PROCESO (409): sin «Reintentar»; a los 10 s se consulta solo a ese agente y se actualiza el resultado y la lista.
  const consultarTrasProceso = (agId) => setTimeout(async () => {
    const c = await llamarAltaAgente({ accion: 'consultar', agente_id: agId });
    setModal((m) => (m && m.tipo === 'alta-ok' && m.agId === agId && komitoEnProceso(m.res)
      ? { ...m, res: { ...m.res, komito: { ...m.res.komito, ...(c?.komito || {}), ...(c?.komito?.ok ? { ok: true, error: null, mensaje: null } : {}) } } } : m));
    setRefrescarEquipo((x) => x + 1);
  }, EN_PROCESO_MS);
  const reintentarAlta = async (m, lado) => {
    setBusy(true);
    const res = await llamarAltaAgente({ accion: 'crear', agente_id: m.agId, solo: lado }); // solicitud_id nuevo
    setBusy(false);
    const nuevoRes = lado === 'app' ? { ...m.res, ...res, komito: m.res?.komito } : { ...m.res, ...res, app: m.res?.app };
    setModal({ ...m, res: nuevoRes });
    if (komitoEnProceso(nuevoRes)) consultarTrasProceso(m.agId);
    setRefrescarEquipo((x) => x + 1);
    await load();
  };
  const abrirAlta = (eq) => setModal({ tipo: 'alta', modalidad: 'basico', prefijoManual: false, equipoAlta: equiposAlta.includes(eq) ? eq : (equiposAlta.includes(equipo) ? equipo : equiposAlta[0]), titan: false });
  // Prefijo automático (sugerir_prefijo) mientras no lo cambien a mano.
  const sugerirPrefijo = (nombre) => {
    clearTimeout(prefijoTimer.current);
    if (String(nombre || '').trim().length < 3) return;
    prefijoTimer.current = setTimeout(async () => {
      const { data, error: e } = await supabase.rpc('sugerir_prefijo', { p_nombre: String(nombre).trim() });
      const p = typeof data === 'string' ? data : (Array.isArray(data) ? (data[0]?.sugerir_prefijo || data[0]?.prefijo || data[0]) : data?.prefijo);
      if (e || !p) return;
      setModal((m) => (m && m.tipo === 'alta' && !m.prefijoManual ? { ...m, prefijo: String(p).toUpperCase() } : m));
    }, 400);
  };

  // ── Bloque 19: invitaciones (Edge Function invitar-agente) ──
  // Bloque 32 (B): si el admin entró por correo (sesión de Supabase Auth), la Edge Function v4 lo identifica por el
  // Bearer que supabase-js manda solo; no se pide usuario/contraseña. Solo el acceso temporal por usuario lo pide.
  const pedirAdminY = async (ag, extra = {}) => {
    if (credAdmin.current) { enviarInvitacion(ag, extra); return; }
    let conSesion = false;
    try { const { data } = await supabase.auth.getSession(); conSesion = !!(data && data.session && data.session.access_token); } catch { conSesion = false; }
    if (conSesion) { enviarInvitacion(ag, { ...extra, porSesion: true }); return; }
    setModal({ tipo: 'admin', ag, extra, usuario: loggedAgent?.usuario || '', password: '' });
  };
  const enviarInvitacion = async (ag, extra = {}) => {
    if (!correoValido(ag.email_publico)) { showToast('error', 'Ese agente no tiene un correo válido. Edítalo primero.'); return; }
    // Hotfix (15-oct): sin chequeo de duplicados en cliente al invitar a un agente existente; la base y la Edge Function
    // ya lo validan y responden CORREO_DUPLICADO si aplica (el chequeo local contaba al propio agente como otro).
    setBusy(true);
    const { data, error: e } = await supabase.functions.invoke('invitar-agente', {
      body: extra.porSesion || !credAdmin.current ? { agente_id: ag.id } : { admin_usuario: credAdmin.current.usuario, admin_password: credAdmin.current.password, agente_id: ag.id },
    });
    let resp = data;
    if (e) { try { resp = await e.context.json(); } catch { resp = null; } }
    setBusy(false);
    if (!resp?.ok) {
      if (resp?.error === 'ADMIN' || resp?.error === 'ROL') credAdmin.current = null;
      if (extra.porSesion && (resp?.error === 'ADMIN' || resp?.error === 'ROL')) { setBusy(false); setModal({ tipo: 'admin', ag, extra: { ...extra, porSesion: false }, usuario: loggedAgent?.usuario || '', password: '' }); return; } // la función no reconoció la sesión: pedir usuario
      const msg = resp?.error === 'CORREO_DUPLICADO' ? MSG_CORREO_DUP : (resp?.mensaje || (e && e.message) || 'No se pudo mandar la invitación.');
      if (extra.altaNueva) setModal({ tipo: 'alta-ok', ...extra.altaNueva, invitacion: `No se mandó la invitación: ${msg} Puedes reenviarla desde su tarjeta.` });
      else { setModal(null); showToast('error', msg); }
      await load();
      return;
    }
    await load();
    if (extra.altaNueva) setModal({ tipo: 'alta-ok', ...extra.altaNueva, invitacion: `Invitación enviada a ${resp.email}.` });
    else { setModal(null); showToast('ok', `${resp.reenvio ? 'Invitación reenviada' : 'Invitación enviada'} a ${resp.email}`); }
  };

  // ── Derivados ──
  const activos = lista.filter((a) => a.activo !== false);
  const kpis = [
    ['Activos', activos.length, C.green],
    ...(esTT ? [
      ['Becados', activos.filter((a) => (a.modalidad_tt || 'basico') === 'basico').length, C.blue],
      ['50-50', activos.filter((a) => a.modalidad_tt === 'premier').length, C.purple],
    ] : []),
    ['Con contraseña', activos.filter((a) => a.contrasena_creada_en).length, C.yellow],
    ['Pausados', lista.length - activos.length, C.muted],
  ];
  const ql = norm(q);
  const coincideQ = (a) => !ql || [a.nombre, a.email_publico, a.folio_prefix].some((x) => norm(x).includes(ql));
  const visibles = activos.filter(coincideQ);
  const pausados = lista.filter((a) => a.activo === false).filter(coincideQ);

  // ── Estilos ──
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 16, marginBottom: 14 };
  const input = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 14, color: C.muted, fontWeight: 600, marginBottom: 6, display: 'block' };
  const btn = (extra) => ({ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12, ...extra });

  // Bloque 19: estado de acceso y botón de invitación por agente.
  const accesoDe = (a) => (a.contrasena_creada_en ? ['Con contraseña', fmtFecha(a.contrasena_creada_en), C.green]
    : a.invitacion_enviada_en ? ['Invitado', fmtFecha(a.invitacion_enviada_en), C.yellow]
    : ['PIN', '', C.muted]);
  const BotonInvitacion = ({ ag }) => {
    if (!ag || ag.contrasena_creada_en || ag.activo === false) return null;
    const sinCorreo = !correoValido(ag.email_publico);
    return (
      <button type="button" className="au-btn au-btn--secondary" disabled={busy || sinCorreo} title={sinCorreo ? 'Primero captura su correo' : 'Manda el correo para que cree su contraseña'}
        onClick={() => pedirAdminY(ag)} style={btn({ opacity: sinCorreo ? 0.5 : 1 })}>
        ✉️ {ag.invitacion_enviada_en ? 'Reenviar invitación' : 'Enviar invitación'}
      </button>
    );
  };

  const ErrorBox = ({ msgs }) => (msgs && msgs.length ? (
    <div className="au-banner au-banner--error" style={{ marginBottom: 12, fontSize: 12 }}>{msgs.map((m) => <div key={m}>{m}</div>)}</div>
  ) : null);
  const set = (patch) => setModal((m) => ({ ...m, ...patch, err: '', errs: [] }));

  const renderModal = () => {
    if (!modal) return null;
    const m = modal;
    if (m.tipo === 'modalidad') {
      const nueva = m.ag.modalidad_tt === 'premier' ? 'basico' : 'premier';
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Cambiar modalidad · ${m.ag.nombre}`}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 14 }}>
            De <strong>{MODALIDAD_TT[m.ag.modalidad_tt] || '—'}</strong> a <strong>{MODALIDAD_TT[nueva]}</strong>.
          </div>
          <div className="au-banner" style={{ fontSize: 12, marginBottom: 14, background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, color: C.text }}>
            Aplica a ventas nuevas desde hoy; los folios anteriores no cambian.
          </div>
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarModalidad(m.ag)} disabled={busy}>Cambiar a {MODALIDAD_TT[nueva]}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'editar') {
      const aviso = avisoCorreo(m.correo, m.ag.id);
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Editar · ${m.ag.nombre}`}>
          <ErrorBox msgs={m.err ? [m.err] : []} />
          <label style={lbl}>Nombre completo</label>
          <input style={{ ...input, marginBottom: 14 }} value={m.nombre} onChange={(e) => set({ nombre: e.target.value })} />
          <label style={lbl}>Correo público</label>
          <input style={{ ...input, marginBottom: 4 }} value={m.correo} onChange={(e) => set({ correo: e.target.value })} />
          {aviso && <div style={{ fontSize: 12, color: C.yellow, marginBottom: 8 }}>{aviso}</div>}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarEdicion(m)} disabled={busy}>Guardar</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'pin') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Cambiar contraseña · ${m.ag.nombre}`}>
          <ErrorBox msgs={m.err ? [m.err] : []} />
          <label style={lbl}>Contraseña nueva (mínimo 6)</label>
          <CampoSecreto C={C} autoComplete="new-password" styleCaja={{ marginBottom: 14 }} style={input} value={m.pin || ''} onChange={(v) => set({ pin: v })} />
          <label style={lbl}>Confirmar contraseña</label>
          <CampoSecreto C={C} autoComplete="new-password" style={input} value={m.pin2 || ''} onChange={(v) => set({ pin2: v })} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarPin(m)} disabled={busy}>Guardar contraseña</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'baja') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`⏸ Pausar · ${m.ag.nombre}`}>
          <ErrorBox msgs={m.err ? [m.err] : []} />
          <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 12 }}>
            Se cierra su sesión y no podrá entrar hasta que lo reactives. Su historial, folios y comisiones se conservan; nada se borra.
          </div>
          <label style={lbl}>Motivo (obligatorio)</label>
          <textarea rows={3} style={{ ...input, height: 'auto', minHeight: 72, padding: '10px 12px', resize: 'vertical' }} value={m.motivo || ''} onChange={(e) => set({ motivo: e.target.value })} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarBaja(m)} disabled={busy} style={{ background: C.red }}>⏸ Pausar</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'reactivar') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`▶ Reactivar · ${m.ag.nombre}`}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 14 }}>Podrá volver a entrar al sistema con su mismo acceso. El historial conserva la pausa anterior.</div>
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarReactivar(m.ag)} disabled={busy}>▶ Reactivar</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'historial') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Historial · ${m.ag.nombre}`} ancho={640}>
          {m.err && <ErrorBox msgs={[m.err]} />}
          {m.cargando ? <div style={{ color: C.muted, padding: 20, textAlign: 'center' }}>Cargando…</div>
            : m.filas.length === 0 ? <div style={{ color: C.muted, padding: 20, textAlign: 'center' }}>Sin cambios registrados.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Fecha', 'Campo', 'Antes → Después', 'Quién'].map((h) => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>)}
                </tr></thead>
                <tbody>{m.filas.map((h, i) => (
                  <tr key={h.id || i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '6px 8px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtFecha(h.en)}</td>
                    <td style={{ padding: '6px 8px', color: C.text, fontWeight: 600 }}>{CAMPO_LABEL[h.campo] || h.campo}</td>
                    <td style={{ padding: '6px 8px', color: C.text }}>{h.campo === 'pin' ? 'cambió' : <>{valorHist(h.campo, h.antes)} → <strong>{valorHist(h.campo, h.despues)}</strong></>}</td>
                    <td style={{ padding: '6px 8px', color: C.muted }}>{h.por || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
        </Modal>
      );
    }
    if (m.tipo === 'admin') {
      const confirmar = () => {
        if (!String(m.usuario || '').trim() || !m.password) { setModal({ ...m, err: 'Escribe tu usuario y tu contraseña de administrador.' }); return; }
        credAdmin.current = { usuario: String(m.usuario).trim(), password: m.password };
        enviarInvitacion(m.ag, m.extra);
      };
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo="Confirma que eres tú">
          <ErrorBox msgs={m.err ? [m.err] : []} />
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Para mandar la invitación a <strong style={{ color: C.text }}>{m.ag.nombre}</strong> ({m.ag.email_publico}) confirma tu acceso de administrador.
            Solo se usa para esta sesión y no se guarda.
          </div>
          <label style={lbl}>Usuario de administrador</label>
          <input style={{ ...input, marginBottom: 14 }} autoComplete="username" value={m.usuario || ''} onChange={(e) => set({ usuario: e.target.value })} />
          <label style={lbl}>Contraseña de administrador</label>
          <CampoSecreto C={C} autoComplete="current-password" style={input} value={m.password || ''} onChange={(v) => set({ password: v })}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }} />
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={confirmar} disabled={busy}>{busy ? 'Enviando…' : '✉️ Enviar invitación'}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'alta') {
      const aviso = avisoCorreo(m.correo, null);
      const eqAlta = equiposAlta.length > 1 ? (m.equipoAlta || equipo) : (equiposAlta[0] || equipo);
      const ttAlta = eqAlta === 'top_tours';
      const correoMal = String(m.correo || '').trim() !== '' && !correoC100(m.correo);
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo={`Nuevo agente · ${NOMBRE_EQUIPO[eqAlta]}`} ancho={520}>
          <BandaBorrador C={C} borrador={borradorAlta} compacta />
          <ErrorBox msgs={m.errs} />
          <label style={lbl}>Nombre completo</label>
          <input style={{ ...input, marginBottom: 14 }} value={m.nombre || ''} onChange={(e) => { set({ nombre: e.target.value }); sugerirPrefijo(e.target.value); }} />
          {/* Bloque 52: equipo — selector solo para Marco; Maggie y Tere lo tienen fijo */}
          <label style={lbl}>Equipo</label>
          {equiposAlta.length > 1
            ? <div style={{ marginBottom: 14 }}><Segmentado valor={eqAlta} opciones={equiposAlta.map((k) => [k, NOMBRE_EQUIPO[k]])} onChange={(k) => set({ equipoAlta: k })} /></div>
            : <div style={{ fontSize: 14, color: C.text, fontWeight: 700, marginBottom: 14 }}>{NOMBRE_EQUIPO[eqAlta]}</div>}
          <label style={lbl}>Correo @cancun100.com (el que creaste en Titan)</label>
          <input style={{ ...input, marginBottom: 4 }} value={m.correo || ''} autoCapitalize="none" spellCheck={false} placeholder="nombre@cancun100.com" onChange={(e) => set({ correo: e.target.value.toLowerCase().replace(/\s/g, '') })} />
          {correoMal && <div style={{ fontSize: 12, color: C.red, marginBottom: 6 }}>Solo correos @cancun100.com.</div>}
          {aviso && <div style={{ fontSize: 12, color: C.red, marginBottom: 6 }}>{MSG_CORREO_DUP} ({aviso.replace(/^Aviso: /, '')})</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={lbl}>Prefijo de folio {m.prefijoManual ? '(2–3 letras)' : '(automático)'}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...input, textTransform: 'uppercase', opacity: m.prefijoManual ? 1 : 0.8 }} maxLength={3} readOnly={!m.prefijoManual} value={m.prefijo || ''}
                  placeholder={m.prefijoManual ? '' : 'Se genera con el nombre'}
                  onChange={(e) => set({ prefijo: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })} />
                {!m.prefijoManual && <button type="button" className="au-btn au-btn--ghost" onClick={() => set({ prefijoManual: true })} style={btn({ whiteSpace: 'nowrap' })}>✎ Cambiar</button>}
              </div>
            </div>
            {ttAlta && (
            <div style={{ flex: '1 1 180px' }}>
              <label style={lbl}>Modalidad</label>
              <Segmentado valor={m.modalidad || 'basico'} opciones={['basico', 'premier'].map((k) => [k, MODALIDAD_TT[k]])} onChange={(k) => set({ modalidad: k })} />
            </div>
            )}
          </div>
          {/* Bloque 52: casilla obligatoria y aviso fijo de los dos correos (sin PIN: nadie toca contraseñas) */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 14, color: C.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!m.titan} onChange={(e) => set({ titan: e.target.checked })} style={{ marginTop: 2, width: 16, height: 16 }} />
            <span><strong>Ya creé este correo en Titan</strong> <span style={{ color: C.muted }}>(obligatorio)</span></span>
          </label>
          <div className="au-banner" style={{ fontSize: 12, marginTop: 12, background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, color: C.text }}>📧 {AVISO_DOS_CORREOS}</div>
          {enSandbox && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>En «Ver como» solo se crea la fila: no se llama a Komito ni se manda invitación.</div>}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setModal(null)} disabled={busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={() => guardarAlta(m)} disabled={busy || !m.titan} style={!m.titan ? { opacity: 0.45, cursor: 'not-allowed' } : undefined} title={!m.titan ? 'Marca «Ya creé este correo en Titan»' : ''}>{busy ? 'Dando de alta…' : 'Dar de alta en la app y en Komito'}</button>
          </div>
        </Modal>
      );
    }
    if (m.tipo === 'ficha') { // Bloque 28
      return <ModalVerFicha C={C} agente={m.ag} ficha={fichas[m.ag.id]} puedeVerSensibles={puedeVerSensibles} quien={quien} onClose={() => setModal(null)} onCambio={(nueva) => setFichas((x) => ({ ...x, [nueva.agente_id]: nueva }))} onRecargar={load} />;
    }
    if (m.tipo === 'alta-ok' && m.agId) { // Bloque 52: alta con Komito
      const ttOk = m.equipoAlta === 'top_tours';
      return (
        <Modal C={C} busy={busy || m.enviando} onClose={() => setModal(null)} titulo={`Alta · ${m.nombre}`} ancho={560}>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, marginBottom: 10 }}>
            <div><strong>Equipo:</strong> {NOMBRE_EQUIPO[m.equipoAlta]} · <strong>Prefijo:</strong> {m.prefijo}{ttOk && <> · <strong>Modalidad:</strong> {MODALIDAD_TT[m.modalidad]}</>}</div>
            <div style={{ fontSize: 12, color: C.green }}>✓ Fila creada en la app</div>
          </div>
          {m.sandbox ? <div style={{ fontSize: 12, color: C.muted }}>En «Ver como» no se llamó a Komito ni se mandó invitación. Desde tu sesión real usa «Reenviar invitación» en Equipo · Agentes.</div>
            : m.enviando ? <div style={{ fontSize: 14, color: C.blue, padding: '8px 0' }}>⏳ Dando de alta en la app y en Komito…</div>
            : <ResultadoAlta C={C} res={m.res} accion="crear" busy={busy} onReintentar={(lado) => reintentarAlta(m, lado)} />}
          <div className="au-banner" style={{ fontSize: 12, marginTop: 10, background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, color: C.text }}>📧 {AVISO_DOS_CORREOS}</div>
          <div className="au-pie"><button type="button" className="au-btn au-btn--primary" onClick={() => setModal(null)} disabled={busy || m.enviando}>Listo</button></div>
        </Modal>
      );
    }
    if (m.tipo === 'alta-ok') {
      return (
        <Modal C={C} busy={busy} onClose={() => setModal(null)} titulo="✅ Agente dado de alta">
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7 }}>
            <div><strong>Usuario:</strong> {m.nombre}</div>
            {!m.invitacion && <div><strong>Contraseña:</strong> la que capturaste</div>}
            <div><strong>Prefijo:</strong> {m.prefijo}{esTT && <> · <strong>Modalidad:</strong> {MODALIDAD_TT[m.modalidad]}</>}</div>
          </div>
          {m.invitacion
            ? <div style={{ fontSize: 12, color: /^Invitación enviada/.test(m.invitacion) ? C.green : C.red, marginTop: 10 }}>{m.invitacion} Entra con su correo cuando cree su contraseña.</div>
            : <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Pásale estos datos al agente para que entre al sistema.</div>}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--primary" onClick={() => setModal(null)}>Listo</button>
          </div>
        </Modal>
      );
    }
    return null;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando agentes Top Tours…</div>;

  return (
    <div style={{ marginTop: 20 }}>
      {toast && (
        <div className={`au-banner ${toast.tipo === 'error' ? 'au-banner--error' : 'au-banner--success'}`} style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 600, fontWeight: 600, boxShadow:'var(--au-shadow-card)' }}>{toast.msg}</div>
      )}
      {renderModal()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', fontFamily: 'var(--au-font-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 3, height: 16, background: 'var(--au-raya)', borderRadius: 2, display: 'inline-block' }} />
          👥 Agentes · {NOMBRE_EQUIPO[equipo]}
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {lista.some(sinFicha) && <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={pedirFichaEquipo} title="Pide la ficha a todos los activos que aún no la tienen">📝 Solicitar ficha a todo el equipo</button>}
          <button type="button" className="au-btn au-btn--primary" onClick={() => abrirAlta(equipo)}>+ Dar de alta agente</button>
        </div>
      </div>
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Bloque 52: Equipo · Agentes (app + Komito). Fuera del «Ver como». */}
      {!enSandbox && <EquipoAgentes C={C} rol={rol} equipoInicial={equipo} onNuevo={abrirAlta} refrescar={refrescarEquipo} onCambio={load} />}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10, marginBottom: 14 }}>
        {kpis.map(([l, v, col]) => (
          <div key={l} style={{ ...card, marginBottom: 0, padding: '16px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--au-text-faint)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: col, fontFamily: 'var(--au-font-text)' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '12px 16px' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, correo o prefijo..." style={{ ...input, paddingLeft: 34 }} />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4 }}>🔍</span>
        </div>
        <div style={{ fontSize: 12, color: C.muted }}>{visibles.length} activo{visibles.length !== 1 ? 's' : ''}{pausados.length > 0 && <> · {pausados.length} en pausa</>}</div>
      </div>

      {/* Lista */}
      {visibles.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: C.muted, padding: 40 }}>{q ? 'Sin agentes activos que coincidan.' : `Sin agentes activos en ${NOMBRE_EQUIPO[equipo]}.`}</div>
      )}
      {visibles.map((a) => tarjeta(a))}

      {/* Bloque 20: pausados, plegados */}
      {pausados.length > 0 && (
        <div style={{ ...card, padding: '10px 16px' }}>
          <button type="button" onClick={() => setVerPausados((v) => !v)} aria-expanded={verPausados}
            style={{ width: '100%', background: 'none', border: 'none', color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '6px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⏸ Pausados ({pausados.length})</span><span style={{ color: C.muted, fontSize: 12 }}>{verPausados ? 'Ocultar ▲' : 'Ver ▼'}</span>
          </button>
          {verPausados && <div style={{ marginTop: 10 }}>{pausados.map((a) => tarjeta(a))}</div>}
        </div>
      )}

      {esTT && <SupervisionTT C={C} card={card} />}
      {pie}
    </div>
  );

  function tarjeta(a) {
        const activo = a.activo !== false;
        const esPremier = a.modalidad_tt === 'premier';
        return (
          <div key={a.id} style={{ ...card, opacity: activo ? 1 : 0.75, borderLeft: `3px solid ${activo ? C.green : C.muted}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ minWidth: 220, flex: '1 1 260px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  {a.nombre}
                  <span className="au-badge au-badge--neutral" style={{ fontSize: 12, marginLeft: 8 }}>{a.folio_prefix || '—'}</span>
                  <ChipAcceso a={a} /> {/* B52-B */}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{a.email_publico || 'sin correo público'}</div>
                {(() => { const [t, fch, col] = accesoDe(a); return (
                  <div style={{ fontSize: 12, marginTop: 4, color: C.muted }}>Acceso: <span style={{ color: col, fontWeight: 700 }}>{t}</span>{fch && <> · {fch}</>}{a.invitacion_por && !a.contrasena_creada_en && <> · por {a.invitacion_por}</>}</div>
                ); })()}
                <div style={{ marginTop: 4 }}><EstadoFicha C={C} agente={a} ficha={fichas[a.id]} conConteo={puedeVerSensibles} /></div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                  {folios[a.id] == null ? '— folios' : `${folios[a.id]} folio${folios[a.id] !== 1 ? 's' : ''}`}
                  {a.alta_por && <> · alta por {a.alta_por}</>}
                </div>
                <div style={{ marginTop: 8 }}>
                  {/* Bloque 52-B: con pausa_at o baja_at el agente sigue «activo» (se ve todo) pero sin acceso */}
                  {activo && (a.pausa_at || a.baja_at) ? (
                      <div style={{ fontSize: 12, color: C.muted }}>
                        {a.baja_at ? <>Sin acceso desde {fmtFecha(a.baja_at)}{a.baja_por && <> · por {a.baja_por}</>}{a.baja_motivo && <div style={{ marginTop: 3 }}>Motivo: {a.baja_motivo}</div>}</>
                          : <>Sin acceso desde {fmtFecha(a.pausa_at)}{a.pausa_por && <> · por {a.pausa_por}</>}{a.pausa_motivo && <div style={{ marginTop: 3 }}>Motivo: {a.pausa_motivo}</div>}</>}
                      </div>
                    )
                    : activo
                    ? <span className="au-badge au-badge--success" style={{ fontSize: 12 }}>Activo</span>
                    : (
                      <div style={{ fontSize: 12, color: C.red }}>
                        {/* Bloque 52: pausa (pausa_*) y baja (baja_*) ya son distintas */}
                        <span className="au-badge au-badge--error" style={{ fontSize: 12, marginRight: 6 }}>{a.baja_at ? '⛔ Baja' : a.pausa_at ? '⏸ En pausa' : 'Inactivo (antiguo)'}</span>
                        {(() => { const at = a.pausa_at || a.baja_at; const por = a.pausa_at ? a.pausa_por : a.baja_por; const mot = a.pausa_at ? a.pausa_motivo : a.baja_motivo; return (<>
                          {at ? fmtFecha(at) : 'sin fecha'}{por && <> · por {por}</>}
                          {mot && <div style={{ color: C.muted, marginTop: 3 }}>Motivo: {mot}</div>}
                        </>); })()}
                      </div>
                    )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                {esTT && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted }}>
                  <span style={{ fontWeight: esPremier ? 400 : 700, color: esPremier ? C.muted : C.text }}>{MODALIDAD_TT.basico}</span>
                  <button type="button" className="au-switch" data-on={esPremier} disabled={busy} onClick={() => setModal({ tipo: 'modalidad', ag: a })} style={{ display: 'inline-block' }} title="Cambiar modalidad">
                    <span className="au-switch__knob" />
                  </button>
                  <span style={{ fontWeight: esPremier ? 700 : 400, color: esPremier ? C.text : C.muted }}>{MODALIDAD_TT.premier}</span>
                </div>
                )}
                {/* Bloque 36 (C): beneficio 🎯 RP VENTA POR FUERA (80 %) — Sala y Top Tours */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted }}>
                  <span style={{ fontWeight: a.puede_rp_por_fuera ? 700 : 400, color: a.puede_rp_por_fuera ? C.text : C.muted }}>🎯 Puede RP por fuera</span>
                  <button type="button" className="au-switch" data-on={!!a.puede_rp_por_fuera} disabled={busy || !activo} title={a.puede_rp_por_fuera ? 'Quitar el beneficio RP (80 %)' : 'Dar el beneficio RP (80 %)'} style={{ display: 'inline-block' }}
                    onClick={() => actualizar(a.id, { puede_rp_por_fuera: !a.puede_rp_por_fuera }, `${a.nombre}: RP por fuera ${a.puede_rp_por_fuera ? 'desactivado' : 'activado'}`)}>
                    <span className="au-switch__knob" />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button type="button" className="au-btn au-btn--secondary" style={btn()} onClick={() => setModal({ tipo: 'editar', ag: a, nombre: a.nombre || '', correo: a.email_publico || '' })}>Editar</button>
                  <button type="button" className="au-btn au-btn--secondary" style={btn()} onClick={() => setModal({ tipo: 'pin', ag: a })}>Cambiar contraseña</button>
                  <button type="button" className="au-btn au-btn--ghost" style={btn()} onClick={() => abrirHistorial(a)}>Historial</button>
                  {onAuditoria && <button type="button" className="au-btn au-btn--ghost" style={btn()} onClick={() => onAuditoria('agentes', a.id, a.nombre)} title="Auditoría de cambios de este agente">🕘 Auditoría</button>}
                  {sinFicha(a)
                    ? <button type="button" className="au-btn au-btn--secondary" style={btn()} disabled={busy} onClick={() => pedirFicha(a)}>📝 Solicitar ficha</button>
                    : <button type="button" className="au-btn au-btn--secondary" style={btn()} onClick={() => setModal({ tipo: 'ficha', ag: a })}>📝 Ver ficha</button>}
                  {gestionaKomito(a)
                    ? <span style={{ fontSize: 12, color: C.muted, alignSelf: 'center' }} title="Bloque 52: pausar, reactivar, baja e invitación van por app + Komito">⏸ ▶ ✉️ en «Equipo · Agentes» ↑</span>
                    : (<>
                      {activo
                        ? <button type="button" className="au-btn au-btn--ghost" style={btn({ color: C.red })} onClick={() => setModal({ tipo: 'baja', ag: a })}>⏸ Pausar</button>
                        : <button type="button" className="au-btn au-btn--secondary" style={btn({ color: C.green })} onClick={() => setModal({ tipo: 'reactivar', ag: a })}>▶ Reactivar</button>}
                      <BotonInvitacion ag={a} />
                    </>)}
                </div>
              </div>
            </div>
          </div>
        );
  }
}
